import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { Calendar as CalendarIcon, MapPin, Users, Share2, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function EventsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/events"); setItems(data); }
    catch (err) { console.error("Failed to load events:", err); }
  })(); }, []);

  const now = new Date();
  const upcoming = items.filter((e) => new Date(e.date) >= now);
  const past = items.filter((e) => new Date(e.date) < now);

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Meetup Komunitas</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">Event & Kopdar</h1>
          <p className="text-bizma-subtext mt-2">Bertemu, belajar, dan tumbuh bersama anggota lain.</p>
        </div>
      </div>

      <section className="mb-10" data-testid="events-upcoming">
        <h2 className="font-display text-xl font-semibold mb-4">Akan Datang</h2>
        {upcoming.length === 0 ? (
          <div className="felt-card p-8 text-center text-bizma-subtext">Belum ada event terjadwal.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcoming.map((e) => <EventCard key={e.event_id} e={e} />)}
          </div>
        )}
      </section>

      {past.length > 0 && (
        <section data-testid="events-past">
          <h2 className="font-display text-xl font-semibold mb-4 text-bizma-subtext">Sudah Lewat</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 opacity-80">
            {past.map((e) => <EventCard key={e.event_id} e={e} />)}
          </div>
        </section>
      )}
    </Layout>
  );
}

function EventCard({ e }) {
  const d = new Date(e.date);
  const shareToWA = (ev) => {
    ev.preventDefault(); ev.stopPropagation();
    const url = `${window.location.origin}/app/events/${e.event_id}`;
    const msg = `📅 *${e.title}*\n${d.toLocaleString("id-ID",{dateStyle:"long",timeStyle:"short"})}${e.location?`\n📍 ${e.location}`:""}\n\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };
  return (
    <Link to={`/app/events/${e.event_id}`} className="felt-card overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-md relative" data-testid={`event-card-${e.event_id}`}>
      <div className="aspect-video bg-bizma-muted">
        {e.photo_path ? <img src={fileUrl(e.photo_path)} alt="" className="w-full h-full object-cover" /> :
          <div className="w-full h-full grid place-items-center text-bizma-subtext"><CalendarIcon /></div>}
      </div>
      {e.is_pengurus_only && (
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-bizma-terracotta text-white font-bold px-2 py-1 rounded-full shadow" data-testid={`event-pengurus-badge-${e.event_id}`}>
          <Shield size={10}/> Kopdar Pengurus
        </div>
      )}
      <button onClick={shareToWA} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-bizma-border grid place-items-center text-bizma-sage hover:text-bizma-terracotta shadow-sm" data-testid={`event-share-${e.event_id}`} aria-label="Bagikan ke WhatsApp"><Share2 size={14}/></button>
      <div className="p-5">
        <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold flex items-center gap-2">
          <CalendarIcon size={14}/> {d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })} • {d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
        </div>
        <div className="font-display font-semibold text-lg mt-1">{e.title}</div>
        {e.location && <div className="text-sm text-bizma-subtext mt-1 flex items-center gap-1"><MapPin size={12}/> {e.location}</div>}
        <div className="mt-3 text-sm text-bizma-subtext flex items-center gap-1"><Users size={14}/> {e.rsvp_count || 0} akan hadir</div>
      </div>
    </Link>
  );
}
